import { PalmettoIcon } from '@palmetto/dsm-components';
import { useCallback } from 'react';
import { useSlate, type RenderElementProps, type RenderLeafProps } from 'slate-react';

import type { MarkFormat } from '../../types/RichTextEditor';

import { isBlockActive, isMarkActive, toggleList, toggleMark } from './editorCommands';

export const renderElement = ({ attributes, children, element }: RenderElementProps) => {
  switch (element.type) {
    case 'bulleted-list':
      return (
        <ul className="list-disc pl-16" {...attributes}>
          {children}
        </ul>
      );
    case 'numbered-list':
      return (
        <ol className="list-decimal pl-16" {...attributes}>
          {children}
        </ol>
      );
    case 'list-item':
      return <li {...attributes}>{children}</li>;
    case 'link':
      return (
        <a href={element.url} className="text-text-action underline" {...attributes}>
          {children}
        </a>
      );
    default:
      return (
        <p className="m-0 mb-4 last:mb-0" {...attributes}>
          {children}
        </p>
      );
  }
};

export const renderLeaf = ({ attributes, children, leaf }: RenderLeafProps) => {
  let node = children;
  if (leaf.bold) node = <strong>{node}</strong>;
  if (leaf.italic) node = <em>{node}</em>;
  return <span {...attributes}>{node}</span>;
};

export const MarkButton = ({ format, label }: { format: MarkFormat; label: string }) => {
  const editor = useSlate();
  const active = isMarkActive(editor, format);
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      toggleMark(editor, format);
    },
    [editor, format],
  );
  return (
    <button
      type="button"
      title={format}
      onMouseDown={handleMouseDown}
      className={`rounded px-6 py-2 text-sm hover:bg-canvas-highlight-bold ${active ? 'bg-canvas-highlight-bold text-text-primary' : 'text-text-secondary'}`}
    >
      {label}
    </button>
  );
};

export const ListButton = ({ format, label }: { format: 'bulleted-list' | 'numbered-list'; label: string }) => {
  const editor = useSlate();
  const active = isBlockActive(editor, format);
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      toggleList(editor, format);
    },
    [editor, format],
  );
  return (
    <button
      type="button"
      title={format}
      onMouseDown={handleMouseDown}
      className={`rounded px-6 py-2 text-sm hover:bg-canvas-highlight-bold ${active ? 'bg-canvas-highlight-bold text-text-primary' : 'text-text-secondary'}`}
    >
      {label}
    </button>
  );
};

export const LinkButton = ({ onMouseDown }: { onMouseDown: (e: React.MouseEvent<HTMLButtonElement>) => void }) => {
  const editor = useSlate();
  const active = isBlockActive(editor, 'link');
  return (
    <button
      type="button"
      title="Link"
      onMouseDown={onMouseDown}
      className={`rounded h-24 px-6 py-2 text-sm hover:bg-canvas-highlight-bold ${active ? 'bg-canvas-highlight-bold text-text-primary' : 'text-text-secondary'}`}
    >
      <PalmettoIcon name="link" size="xs" />
    </button>
  );
};
